import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Star, Users, Minus, Plus, Calendar, Clock, CalendarCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";

function dates() {
  const out = [];
  for (let i = 0; i < 5; i++) { const d = new Date(); d.setDate(d.getDate() + i); out.push({ iso: d.toISOString().slice(0, 10), label: i === 0 ? "Today" : i === 1 ? "Tomorrow" : d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric" }) }); }
  return out;
}

export default function DineOut() {
  const nav = useNavigate();
  const [rests, setRests] = useState([]);
  const [sel, setSel] = useState(null);
  const [avail, setAvail] = useState(null);
  const [date, setDate] = useState(dates()[0].iso);
  const [time, setTime] = useState(null);
  const [guests, setGuests] = useState(2);
  const [myRes, setMyRes] = useState([]);
  const [showMine, setShowMine] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadMine = () => api.get("/dineout/reservations").then((r) => setMyRes(r.data)).catch(() => {});
  useEffect(() => { api.get("/dineout/restaurants").then((r) => setRests(r.data)).catch(() => {}); loadMine(); }, []);
  useEffect(() => { if (sel) { setTime(null); api.get(`/dineout/restaurants/${sel.id}/availability`, { params: { date } }).then((r) => setAvail(r.data)).catch(() => {}); } }, [sel, date]);

  const reserve = async () => {
    if (!time) return toast.error("Pick a time slot");
    setBusy(true);
    try { const { data } = await api.post("/dineout/reserve", { restaurant_id: sel.id, date, time, guests }); toast.success(`Table booked! ${data.code}`); setSel(null); loadMine(); setShowMine(true); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const cancel = async (id) => { await api.post(`/dineout/reservations/${id}/cancel`); toast.success("Reservation cancelled"); loadMine(); };

  return (
    <div className="min-h-screen bg-surface max-w-[520px] mx-auto pb-10">
      <div className="sticky top-0 z-40 bg-gradient-to-r from-purple-700 to-fuchsia-600 text-white px-3 py-3 rounded-b-3xl flex items-center gap-2">
        <button onClick={() => (sel ? setSel(null) : nav("/"))} className="w-9 h-9 flex items-center justify-center tap"><ArrowLeft size={22} /></button>
        <div className="flex-1"><div className="font-display font-extrabold text-lg">BiteGoFood DineOut</div><div className="text-[11px] text-white/85">Reserve a table & save on dine-in</div></div>
        <button onClick={() => setShowMine((s) => !s)} className="text-xs font-semibold bg-white/15 px-3 py-1.5 rounded-full" data-testid="my-reservations-btn">My Bookings</button>
      </div>

      {showMine && (
        <div className="px-4 py-3">
          <h3 className="font-bold text-ink mb-2">My reservations</h3>
          {myRes.length === 0 ? <p className="text-sm text-ink-600">No reservations yet.</p> :
            myRes.map((r) => (
              <div key={r.id} className="bg-white rounded-2xl border border-border p-3 mb-2 flex items-center gap-3" data-testid={`reservation-${r.id}`}>
                <img src={r.restaurant_image} alt="" className="w-12 h-12 rounded-xl object-cover" />
                <div className="flex-1 min-w-0"><div className="font-bold text-sm truncate">{r.restaurant_name}</div><div className="text-xs text-ink-600">{r.date} · {r.time} · {r.guests} guests</div><div className="text-[11px] text-purple-700 font-semibold">{r.code} · {r.status}</div></div>
                {r.status !== "cancelled" && <button onClick={() => cancel(r.id)} className="text-xs font-bold text-bad">Cancel</button>}
              </div>
            ))}
        </div>
      )}

      {!sel ? (
        <div className="px-4 py-3 grid gap-4">
          {rests.map((r) => (
            <button key={r.id} onClick={() => setSel(r)} className="text-left tap" data-testid={`dineout-rest-${r.id}`}>
              <div className="relative rounded-2xl overflow-hidden"><img src={r.cover} alt="" className="w-full h-36 object-cover" /><div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" /><div className="absolute bottom-2 left-3 right-3 text-white"><div className="font-bold">{r.name}</div><div className="text-xs flex items-center gap-2"><span className="flex items-center gap-0.5"><Star size={11} className="fill-gold text-gold" />{r.rating}</span>· {r.cuisine}</div></div><div className="absolute top-2 left-2 bg-purple-700 text-white text-[10px] font-bold px-2 py-1 rounded-md">{r.dineout_offer}</div></div>
            </button>
          ))}
        </div>
      ) : (
        <div className="px-4 py-3">
          <img src={sel.cover} alt="" className="w-full h-40 object-cover rounded-2xl" />
          <h2 className="font-display font-bold text-xl mt-2">{sel.name}</h2>
          <p className="text-sm text-ink-600">{sel.cuisine} · {sel.address}</p>
          <div className="bg-purple-50 text-purple-700 rounded-xl px-3 py-2 text-sm font-bold mt-2">{sel.dineout_offer}</div>

          <h3 className="font-bold text-ink mt-4 flex items-center gap-1"><Calendar size={16} /> Date</h3>
          <div className="flex gap-2 overflow-x-auto hide-scrollbar mt-2">{dates().map((d) => <button key={d.iso} onClick={() => setDate(d.iso)} className={`px-3 py-2 rounded-xl text-sm font-semibold whitespace-nowrap border ${date === d.iso ? "bg-purple-700 text-white border-purple-700" : "bg-white border-border"}`}>{d.label}</button>)}</div>

          <h3 className="font-bold text-ink mt-4 flex items-center gap-1"><Users size={16} /> Guests</h3>
          <div className="flex items-center gap-3 mt-2"><button onClick={() => setGuests((g) => Math.max(1, g - 1))} className="w-10 h-10 rounded-xl bg-white border border-border flex items-center justify-center"><Minus size={16} /></button><span className="font-bold text-lg w-8 text-center">{guests}</span><button onClick={() => setGuests((g) => Math.min(20, g + 1))} className="w-10 h-10 rounded-xl bg-white border border-border flex items-center justify-center"><Plus size={16} /></button></div>

          <h3 className="font-bold text-ink mt-4 flex items-center gap-1"><Clock size={16} /> Time</h3>
          <div className="grid grid-cols-3 gap-2 mt-2">{avail?.slots.map((s) => <button key={s.time} disabled={s.seats_left < guests} onClick={() => setTime(s.time)} className={`rounded-xl py-2.5 text-sm font-semibold border disabled:opacity-40 ${time === s.time ? "bg-purple-700 text-white border-purple-700" : "bg-white border-border"}`} data-testid={`slot-${s.time}`}>{s.time}<div className="text-[10px] font-normal">{s.seats_left} seats</div></button>)}</div>

          <button onClick={reserve} disabled={busy} className="w-full bg-purple-700 text-white font-bold py-3.5 rounded-xl mt-5 flex items-center justify-center gap-2 tap" data-testid="reserve-btn"><CalendarCheck size={18} /> {busy ? "Reserving…" : "Confirm Reservation"}</button>
        </div>
      )}
    </div>
  );
}
